import React, { useEffect, useState } from 'react';
import { StarField } from './Brand';
import { Logo3D } from './Logo3D';
import { gateStatus, verifyGate } from '../lib/api';

const GATE_KEY = 'auralis_gate_ok';

export function SiteGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    gateStatus()
      .then((s) => {
        if (!active) return;
        if (!s.gate_enabled || localStorage.getItem(GATE_KEY) === '1') {
          setUnlocked(true);
        }
      })
      .catch(() => {
        // If the status check fails, fall back to a stored unlock so we don't lock people out on a blip
        if (active && localStorage.getItem(GATE_KEY) === '1') setUnlocked(true);
      })
      .finally(() => active && setChecking(false));
    return () => {
      active = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await verifyGate(password.trim());
      if (res.success) {
        localStorage.setItem(GATE_KEY, '1');
        setUnlocked(true);
      } else {
        setError('Incorrect password. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen aura-space grid place-items-center">
        <i className="fa-solid fa-circle-notch fa-spin text-2xl text-white/70" />
      </div>
    );
  }

  if (unlocked) return children;

  return (
    <div className="min-h-screen aura-space relative flex items-center justify-center px-4 py-10 overflow-hidden">
      <StarField count={70} />
      <div className="aura-orb animate-aurora" style={{ width: 300, height: 300, top: '-8%', left: '-8%', background: 'rgba(168,85,247,0.4)' }} />
      <div className="aura-orb animate-aurora" style={{ width: 220, height: 220, bottom: '2%', right: '-6%', background: 'rgba(236,72,153,0.3)', animationDelay: '3s' }} />

      <div className="relative w-full max-w-sm animate-fade-up">
        <div className="flex flex-col items-center mb-6">
          <Logo3D size={92} />
          <div className="flex items-baseline gap-2 mt-2">
            <span className="font-display text-3xl text-white font-bold">Auralis</span>
            <span className="font-display text-3xl aura-gradient-text font-light">Health</span>
          </div>
        </div>

        <div className="glass-dark rounded-3xl p-6 sm:p-7" style={{ boxShadow: '0 30px 60px -20px rgba(11,11,31,0.7)' }}>
          <div className="grid place-items-center w-12 h-12 rounded-2xl mx-auto mb-4 bg-[rgba(168,85,247,0.2)] text-[var(--aura-purple-light)]">
            <i className="fa-solid fa-lock" />
          </div>
          <h1 className="font-display text-xl text-white text-center mb-1">Private testing preview</h1>
          <p className="text-white/60 text-sm text-center mb-6">This site is invite-only while we test. Enter the access password to continue.</p>

          <form onSubmit={submit}>
            <div className={`flex items-center gap-3 rounded-2xl px-4 mb-3 border transition-base ${error ? 'border-red-400/60 bg-red-500/10' : 'border-white/10 bg-white/5'}`}>
              <i className="fa-solid fa-key text-white/40" />
              <input
                data-testid="gate-password-input"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Access password"
                autoFocus
                className="flex-1 bg-transparent py-4 text-white placeholder-white/40 outline-none"
              />
            </div>
            {error && <p data-testid="gate-error" className="text-red-300 text-xs mb-3 text-center">{error}</p>}
            <button
              data-testid="gate-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 disabled:opacity-70 transition-base"
            >
              {submitting ? <i className="fa-solid fa-circle-notch fa-spin" /> : <>Enter site <i className="fa-solid fa-arrow-right" /></>}
            </button>
          </form>
        </div>
        <p className="text-center text-white/40 text-xs mt-5">Need access? Contact the Auralis team.</p>
      </div>
    </div>
  );
}
